import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Table, Tag } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import defaults from '../../utils/defaults';
import ProgressBars from '../../components/MXCard/ProgressBars';
import EmptyStateMaintenance from '../../assets/emptyState/empty-state-maintenance.svg';
import styles from './WorkpackProfile.module.less';

class ScheduledTable extends PureComponent {
  static propTypes = {
    dataSource: PropTypes.array.isRequired,
    userSettings: PropTypes.object,
    extraColumns: PropTypes.array,
    globalFilter: PropTypes.string,
    selectedAircraft: PropTypes.object,
    intl: PropTypes.object.isRequired,
  };

  static defaultProps = {
    extraColumns: [],
    userSettings: defaults.defaultDateFormat,
    globalFilter: '',
    selectedAircraft: {},
  };

  renderScheduledColumns() {
    const {
      intl: { formatMessage },
      selectedAircraft,
      userSettings: { dateFormat },
      globalFilter: globalFilterProp,
    } = this.props;
    const aircraftThreshold = selectedAircraft ? selectedAircraft.mx_thresholds : {};
    const aircraftAPUEnabled = selectedAircraft ? selectedAircraft.apu_installed : false;
    const globalFilter = globalFilterProp.toLowerCase();
    const itemRefColumn = {
      title: formatMessage({ id: 'title.itemRef' }),
      dataIndex: 'name',
      rowKey: 'name_sorter',
      width: 200,
      sorter: (a, b) => {
        if (!a.name) {
          return -1;
        }
        if (!b.name) {
          return 1;
        }
        return a.name.localeCompare(b.name);
      },
      filteredValue: [globalFilter],
      onFilter: (value, record) => {
        // Global filters for all items, as they filters with filteredValue should be placed in only one column
        let outReturn = false;
        if (record.name && !outReturn) {
          outReturn = record.name.toLowerCase().includes(globalFilter.toLowerCase());
        }
        if (record.reference && !outReturn) {
          outReturn = record.reference.toLowerCase().includes(globalFilter.toLowerCase());
        }
        if (record.mx_type && !outReturn) {
          outReturn = record.mx_type.toLowerCase().includes(globalFilter.toLowerCase());
        }
        if (!outReturn) {
          outReturn = globalFilter === '';
        }
        return outReturn;
      },
      render: (text, row) => {
        if (row.mx_type === null) {
          return (
            <div>
              <Tag>{text}</Tag>
            </div>
          );
        }
        return (
          <div>
            <p className={styles.itemRefTitle}>{text}</p>
            {row.reference === null || row.reference.length === 0 ? (
              ''
            ) : (
              <Tag color="blue" className={styles.taskRefTag}>
                {row.reference}
              </Tag>
            )}
          </div>
        );
      },
    };
    const typeColumn = {
      title: formatMessage({ id: 'title.type' }),
      dataIndex: 'mx_type',
      width: 150,
      render: (text) => {
        if (!text) {
          return '-';
        }
        if (text.includes('apu')) {
          return text
            .replace(/_/g, ' ')
            .replace(' scheduled', '')
            .toUpperCase();
        }
        return (text.charAt(0).toUpperCase() + text.slice(1)).replace(/_/g, ' ').replace(' scheduled', '');
      },
    };
    const flightHrsColumn = {
      title: formatMessage({ id: 'title.flightHrs' }),
      dataIndex: 'flight_seconds_repeat',
      rowKey: 'flight_seconds_repeat',
      width: 150,
      render: (text, row) => {
        if (row.flight_seconds_repeat || row.flight_seconds_due) {
          return (
            <div className={styles.scheduledDetails}>
              {row.flight_seconds_repeat ? (
                <p>
                  {formatMessage({ id: 'title.interval' })}:
                  <span className={styles.columnSpan}>
                    {row.flight_seconds_repeat / 3600} {formatMessage({ id: 'period.hrs' })}
                    {row.flight_seconds_threshold && row.flight_seconds_threshold >= row.flight_seconds_due ? (
                      <span className={styles.secondarySpan}>
                        {' '}
                        ({formatMessage({ id: 'text.thresholdShort' })} {row.flight_seconds_threshold / 3600}{' '}
                        {formatMessage({ id: 'period.hrs' })})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                </p>
              ) : (
                ''
              )}
              {row.flight_seconds_due ? (
                <p>
                  {formatMessage({ id: 'text.nextDue' })}:
                  <span className={styles.columnSpan}>
                    {Math.round(row.flight_seconds_due / 3600)} {formatMessage({ id: 'period.hrs' })}
                    {row.flight_seconds_tolerance ? (
                      <span className={styles.secondarySpan}>
                        {' '}
                        (+/- {Math.abs(row.flight_seconds_tolerance / 3600)} {formatMessage({ id: 'period.hrs' })})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                </p>
              ) : (
                ''
              )}
            </div>
          );
        }
        return '-';
      },
      sorter: (a, b) => a.flight_seconds_due - b.flight_seconds_due,
    };
    const cyclesColumn = {
      title: formatMessage({ id: 'title.cycles' }),
      dataIndex: 'cycles_repeat',
      rowKey: 'cycles_repeat',
      width: 150,
      render: (text, row) => {
        if (row.cycles_repeat || row.cycles_due) {
          return (
            <div className={styles.scheduledDetails}>
              {row.cycles_repeat ? (
                <p>
                  {formatMessage({ id: 'title.interval' })}:
                  <span className={styles.columnSpan}>
                    {row.cycles_repeat} {formatMessage({ id: 'units.cycles' })}
                    {row.cycles_threshold && row.cycles_threshold >= row.cycles_due ? (
                      <span className={styles.secondarySpan}>
                        {' '}
                        ({formatMessage({ id: 'text.thresholdShort' })} {Math.abs(row.cycles_threshold)}{' '}
                        {formatMessage({ id: 'units.cycles' })})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                </p>
              ) : (
                ''
              )}
              {row.cycles_due ? (
                <p>
                  {formatMessage({ id: 'text.nextDue' })}:
                  <span className={styles.columnSpan}>
                    {row.cycles_due} {formatMessage({ id: 'period.cycles' })}
                    {row.cycles_tolerance ? (
                      <span className={styles.secondarySpan}>
                        {' '}
                        (+/- {Math.abs(row.cycles_tolerance)} {formatMessage({ id: 'period.cycles' })})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                </p>
              ) : (
                ''
              )}
            </div>
          );
        }
        return '-';
      },
      sorter: (a, b) => a.cycles_due - b.cycles_due,
    };
    const monthsColumn = {
      title: formatMessage({ id: 'title.months' }),
      dataIndex: 'months_repeat',
      rowKey: 'months_repeat',
      width: 150,
      render: (text, row) => {
        if (row.months_repeat || row.date_due) {
          return (
            <div className={styles.scheduledDetails}>
              {row.months_repeat ? (
                <p>
                  {formatMessage({ id: 'title.interval' })}:
                  <span className={styles.columnSpan}>
                    {row.months_repeat} {formatMessage({ id: 'units.months' })}
                    {row.months_threshold &&
                    row.months_threshold >= Math.abs(moment(row.date_due).diff(moment(), 'months', true)) ? (
                      <span className={styles.secondarySpan}>
                        {' '}
                        ({formatMessage({ id: 'text.thresholdShort' })} {Math.abs(row.months_threshold)}{' '}
                        {formatMessage({ id: 'units.months' })})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                </p>
              ) : (
                ''
              )}
              {row.date_due ? (
                <p>
                  {formatMessage({ id: 'text.nextDue' })}:
                  <span className={styles.columnSpan}>
                    {moment(row.date_due).format(dateFormat)}
                    {row.months_tolerance ? (
                      <span className={styles.secondarySpan}>
                        {' '}
                        (+/- {Math.abs(row.months_tolerance)} {formatMessage({ id: 'units.months' })})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                </p>
              ) : (
                ''
              )}
            </div>
          );
        }
        return '-';
      },
      sorter: (a, b) => {
        if (!a.date_due) {
          return -1;
        }
        if (!b.date_due) {
          return 1;
        }
        return moment(a.date_due) - moment(b.date_due);
      },
    };
    const apuHrsColumn = {
      title: formatMessage({ id: 'title.apuHours' }),
      dataIndex: 'apu_seconds_repeat',
      rowKey: 'apu_seconds_repeat',
      width: 150,
      render: (text, row) => {
        if (row.apu_seconds_repeat || row.apu_seconds_due) {
          return (
            <div className={styles.scheduledDetails}>
              {row.apu_seconds_repeat ? (
                <p>
                  {formatMessage({ id: 'title.interval' })}:
                  <span className={styles.columnSpan}>
                    {Math.abs(row.apu_seconds_repeat / 3600)} {formatMessage({ id: 'period.hrs' })}
                    {row.apu_seconds_threshold && row.apu_seconds_threshold >= row.apu_seconds_due ? (
                      <span className={styles.secondarySpan}>
                        {' '}
                        ({formatMessage({ id: 'text.thresholdShort' })}
                        {Math.abs(row.apu_seconds_threshold / 3600)} {formatMessage({ id: 'period.hrs' })})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                </p>
              ) : (
                ''
              )}
              {row.apu_seconds_due ? (
                <p>
                  {formatMessage({ id: 'text.nextDue' })}:
                  <span className={styles.columnSpan}>
                    {Math.abs(Math.round(row.apu_seconds_due / 3600))} {formatMessage({ id: 'period.hrs' })}
                    {row.apu_seconds_tolerance ? (
                      <span className={styles.secondarySpan}>
                        {' '}
                        (+/- {Math.abs(row.apu_seconds_tolerance / 3600)} {formatMessage({ id: 'period.hrs' })})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                </p>
              ) : (
                ''
              )}
            </div>
          );
        }
        return '-';
      },
      sorter: (a, b) => a.apu_seconds_due - b.apu_seconds_due,
    };
    const estDateColumn = {
      title: formatMessage({ id: 'title.estimatedDue' }),
      dataIndex: 'estimated_date',
      rowKey: 'estimated_date',
      width: 200,
      sorter: (a, b) => {
        if (!a.estimated_date) {
          return -1;
        }
        if (!b.estimated_date) {
          return 1;
        }
        return moment(a.estimated_date) - moment(b.estimated_date);
      },
      render: (text, row) => {
        if (text) {
          return (
            <div className={styles.scheduledDetails}>
              <p>
                {formatMessage({ id: 'text.estimatedShort' })}:
                <span className={styles.columnSpan}>{moment(text).format(dateFormat)}</span>
              </p>
            </div>
          );
        }
        if (row.date_due) {
          return (
            <div className={styles.scheduledDetails}>
              <p>
                {formatMessage({ id: 'text.due' })}:
                <span className={styles.columnSpan}>{moment(row.date_due).format(dateFormat)}</span>
              </p>
            </div>
          );
        }
        return (
          <div>
            <p>-</p>
          </div>
        );
      },
    };
    const statusColumn = {
      title: formatMessage({ id: 'title.status' }),
      dataIndex: 'flight_seconds_remaining',
      rowKey: 'flight_seconds_remaining',
      width: 300,
      render: (text, row) => {
        return (
          <ProgressBars
            mxItem={row}
            hasAPU={aircraftAPUEnabled}
            aircraftThreshold={aircraftThreshold}
            data-test="progressBars"
          />
        );
      },
    };
    const columnsArray = [
      itemRefColumn,
      typeColumn,
      flightHrsColumn,
      cyclesColumn,
      monthsColumn,
      estDateColumn,
      statusColumn,
    ];
    if (selectedAircraft && selectedAircraft.apu_installed) {
      columnsArray.splice(5, 0, apuHrsColumn);
    }
    return columnsArray;
  }

  render() {
    const {
      dataSource,
      extraColumns,
      intl: { formatMessage },
    } = this.props;
    return (
      <Table
        title={() => <div className={styles.tableTitle}>{formatMessage({ id: 'text.scheduledItems' })}</div>}
        columns={this.renderScheduledColumns().concat(extraColumns)}
        scroll={{ x: 1360 }}
        className={styles.defaultTable}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        rowKey={(item) => item.id}
        dataSource={dataSource}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.props}
        locale={{
          emptyText: (
            <div className={styles.emptyText}>
              <img src={EmptyStateMaintenance} alt="empty state" />
              <span>No Data</span>
            </div>
          ),
        }}
      />
    );
  }
}

const scheduledTableWithRedux = connect(({ userSettings }) => ({
  userSettings,
}))(ScheduledTable);

export default injectIntl(scheduledTableWithRedux);
